import React from "react";
import { Trophy } from "lucide-react";

// Small chips shown next to the realtor's name (source of record) and
// in the Scans column of the realtors table.

const SOURCE_LABELS = {
  realtor_ca: "Realtor.ca",
  brokerage: "Brokerage",
  manual: "Manual",
  open_house_listing: "Open House",
  isn: "ISN",
};
const SOURCE_CLASSES = {
  realtor_ca: "bg-violet-50 text-violet-700 border-violet-200",
  brokerage: "bg-sky-50 text-sky-700 border-sky-200",
  manual: "bg-slate-100 text-slate-700 border-slate-200",
  open_house_listing: "bg-amber-50 text-amber-700 border-amber-200",
  isn: "bg-teal-50 text-teal-700 border-teal-200",
};

export function SourceBadge({ source, compact = false }) {
  const key = source && SOURCE_LABELS[source] ? source : "brokerage";
  const label = SOURCE_LABELS[key];
  return (
    <span
      className={`shrink-0 inline-flex items-center rounded border ${SOURCE_CLASSES[key]} ${
        compact ? "px-1.5 py-0 text-[10px]" : "px-2 py-0.5 text-xs"
      }`}
      data-testid={`realtor-source-${key}`}
      title={`Source: ${label}`}
    >
      {label}
    </span>
  );
}

// Scan-count pill — how many times the enrichment pipeline has scanned
// this realtor. Colour-coded so an admin can spot at a glance who's
// burning credits without turning up socials.
//   • 0        → emerald "New" pill (Enrich All targets these via new_only)
//   • 1        → default slate
//   • 2-4      → slate (fine — still learning)
//   • 5+ with 0 socials → amber (probably no discoverable online presence)
export function ScanCountBadge({ realtor }) {
  const count = Number(realtor.scan_count) || 0;
  const filled = [
    "facebook_url", "instagram_url", "linkedin_url", "twitter_url",
    "tiktok_url", "youtube_url", "threads_url",
  ].filter((f) => (realtor[f] || "").trim()).length;
  let cls = "bg-slate-100 text-slate-700";
  let label = String(count);
  let tip = `Scanned ${count} time${count === 1 ? "" : "s"}`;
  if (count === 0) {
    cls = "bg-emerald-50 text-emerald-700 border border-emerald-200";
    label = "New";
    tip = "Never scanned — ideal target for 'New realtors only' mode";
  } else if (count >= 5 && filled === 0) {
    cls = "bg-amber-50 text-amber-800 border border-amber-200";
    tip = `Scanned ${count} times but no socials found — likely no online presence`;
  }
  return (
    <span
      className={`inline-flex items-center justify-center min-w-[28px] px-1.5 py-0.5 rounded-full text-[10px] font-medium ${cls}`}
      title={tip}
      data-testid={`scan-count-${realtor.id}`}
    >
      {label}
    </span>
  );
}


// Gold "Top Performer" chip — set by the award-search job. Click opens the
// first source page so the user can verify the award match by hand.
export function TopPerformerBadge({ realtor, compact = false }) {
  const tp = realtor?.top_performer;
  if (!tp?.is_top) return null;
  const src = (tp.sources || [])[0];
  const title = src?.title
    ? `Top performer — found on: ${src.title}${src.url ? `\n${src.url}` : ""}`
    : "Top performer (award search match)";
  return (
    <span
      role={src?.url ? "link" : undefined}
      tabIndex={src?.url ? 0 : undefined}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        if (src?.url) window.open(src.url, "_blank", "noopener,noreferrer");
      }}
      className={`shrink-0 inline-flex items-center gap-0.5 rounded border bg-amber-50 text-amber-700 border-amber-300 font-medium ${
        compact ? "px-1 py-0 text-[10px]" : "px-1.5 py-0.5 text-[11px]"
      } ${src?.url ? "cursor-pointer hover:bg-amber-100" : ""}`}
      title={title}
      data-testid={`top-performer-badge-${realtor.id}`}
    >
      <Trophy className={compact ? "h-2.5 w-2.5" : "h-3 w-3"} />
      Top
    </span>
  );
}
